import { format, fromUnixTime } from "date-fns/esm";
import Skeleton from "react-loading-skeleton";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ProgressBar from "ds/components/ProgressBar";
import Typography from "ds/components/Typography";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import { Clock, TickCircleFilled } from "components/icons/generated";
import Icon from "ds/components/Icon";
import CardWrapper from "ds/components/Card/components/Wrapper";
import { UserGuideGroupEnriched } from "shared/UserGuides/types";
import useUserGuideCertificates from "shared/UserGuides/useUserGuideCertificates";
import useUserGuideDownloadCertificate from "shared/UserGuides/useUserGuideDownloadCertificate";
import { UNLOCK_CERTIFICATION_EVENT_NAME } from "shared/UserGuides/analytics";
import useAnalytics from "hooks/useAnalytics";
import badgeLockedSrc from "shared/UserGuides/assets/badge-locked.svg";
import badgeUnlockedSrc from "shared/UserGuides/assets/badge-unlocked.svg";
import { showCertificateDetailsDrawer } from "views/Account/MissionControl/CertificateDetailsDrawer";
import Tooltip from "ds/components/Tooltip";

import { showGenerateCertificateModal } from "./GenerateCertificateModal";
import styles from "./styles.module.css";

type LaunchPadUserGuidesGroupSidePanelProps = {
  userGuideGroup: UserGuideGroupEnriched | undefined;
};

const LaunchPadUserGuidesGroupSidePanel = ({
  userGuideGroup,
}: LaunchPadUserGuidesGroupSidePanelProps) => {
  const { getCertificateByGroupId, loading: certsLoading } = useUserGuideCertificates();
  const trackSegmentEvent = useAnalytics();

  const groupId = userGuideGroup?.id;
  const certificate = groupId ? getCertificateByGroupId(groupId) : undefined;
  const { download, isDownloading } = useUserGuideDownloadCertificate(
    certificate,
    groupId ? { mission: groupId } : undefined
  );

  if (!userGuideGroup || certsLoading) {
    return (
      <Box direction="column" gap="x-large">
        <CardWrapper>
          <Box direction="column" padding="large" gap="xx-large">
            <Skeleton circle width="7.2rem" height="7.2rem" />
            <Box direction="column" gap="x-large">
              <Box direction="column" gap="small">
                <Skeleton width="14rem" height="1rem" />
                <Skeleton width="90%" height="1.6rem" />
              </Box>
              <Skeleton width="11rem" height="1.2rem" />
            </Box>
          </Box>
        </CardWrapper>
        <Box gap="medium" align="center" grow="1">
          <Box grow="1">
            <Skeleton height="0.6rem" />
          </Box>
          <Skeleton width="3rem" height="1.2rem" />
        </Box>
      </Box>
    );
  }

  const { guidesCount, guidesCompletedCount, timeToComplete, name } = userGuideGroup;

  const allGuidesCompleted = guidesCount > 0 && guidesCompletedCount === guidesCount;
  // Once a certificate is earned, treat it as complete even if chapters are restarted
  const isComplete = allGuidesCompleted || !!certificate;
  const isUnlocked = !!certificate;
  const isReadyToUnlock = allGuidesCompleted && !certificate;

  const handleUnlock = () => {
    trackSegmentEvent(UNLOCK_CERTIFICATION_EVENT_NAME, { mission: userGuideGroup.id });
    showGenerateCertificateModal({ groupId: userGuideGroup.id });
  };

  const effectiveCompleted = isComplete ? guidesCount : guidesCompletedCount;

  const progressLabel = isComplete
    ? "100% complete 🎉"
    : `${guidesCount > 0 ? ((guidesCompletedCount * 100) / guidesCount).toFixed(0) : 0}%`;

  return (
    <Box direction="column" gap="x-large">
      <Tooltip
        active={!isReadyToUnlock && !isComplete}
        on={(tooltipProps) => (
          <CardWrapper {...tooltipProps}>
            <Box direction="column" padding="large" className={styles.cardContent}>
              <Box direction="column" gap="xx-large">
                <img
                  src={isUnlocked ? badgeUnlockedSrc : badgeLockedSrc}
                  alt=""
                  className={styles.badge}
                  aria-hidden
                />

                <Box direction="column" gap="x-large">
                  <Box direction="column" gap="small">
                    <Typography tag="p" variant="p-t8" color="secondary">
                      {isUnlocked ? "COURSE CERTIFICATE" : "EARN COURSE CERTIFICATE"}
                    </Typography>
                    <Typography tag="p" variant="p-t6">
                      {name}
                    </Typography>
                  </Box>

                  <Box align="center" gap="small">
                    {certificate && (
                      <>
                        <Icon src={TickCircleFilled} size="default" color="secondary" />
                        <Typography tag="span" variant="p-body3" color="secondary">
                          Issued on {format(fromUnixTime(certificate.issuedAt), "MM/dd/yyyy")}
                        </Typography>
                      </>
                    )}
                    {!certificate && allGuidesCompleted && (
                      <>
                        <Icon src={TickCircleFilled} size="default" color="secondary" />
                        <Typography tag="span" variant="p-body3" color="secondary">
                          Complete
                        </Typography>
                      </>
                    )}
                    {!certificate && !allGuidesCompleted && (
                      <>
                        <Icon src={Clock} size="default" color="secondary" />
                        <Tooltip
                          on={(props) => (
                            <Typography {...props} variant="p-body3" tag="span" color="secondary">
                              Complete in {timeToComplete}
                            </Typography>
                          )}
                        >
                          Estimated time to complete: {timeToComplete}
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              {isReadyToUnlock && (
                <Button
                  variant="secondary"
                  size="medium"
                  onPress={handleUnlock}
                  className={styles.unlockButton}
                >
                  Unlock
                </Button>
              )}

              {isUnlocked && certificate && (
                <DropdownMenuEllipsis tooltip="Certificate actions" className={styles.unlockButton}>
                  <DropdownMenuItem
                    onAction={() => showCertificateDetailsDrawer({ certificateId: certificate.id })}
                  >
                    See details
                  </DropdownMenuItem>
                  <DropdownMenuItem onAction={download} isDisabled={isDownloading}>
                    Download as PNG
                  </DropdownMenuItem>
                </DropdownMenuEllipsis>
              )}
            </Box>
          </CardWrapper>
        )}
      >
        Complete all mission objectives to unlock your certificate.
      </Tooltip>

      <Box gap="medium" align="center" grow="1">
        <ProgressBar
          current={effectiveCompleted}
          max={guidesCount}
          fullWidth
          hideLabel
          ariaLabel="Mission completion progress"
        />
        <Typography tag="span" variant="p-t7">
          {progressLabel}
        </Typography>
      </Box>
    </Box>
  );
};

export default LaunchPadUserGuidesGroupSidePanel;
